/**
 * Documents reducer slice. Handles:
 *   trade.ContractCaptured             — contract aggregate
 *   document.Created                   — new doc enters projection (guarded)
 *   document.Hashed / Attested / etc.  — monotonic lifecycle progression
 *   document.BatchAttested             — bulk-flip for T2/T3 montage
 *   document.ConsistencyReportProduced — reconciliation report header
 *   policy.Decided                     — reconciliation cell side-projection
 *     (CrossDocumentConsistencyCheck + WeightToleranceRule gates)
 *
 * Pure function: `(state, ev) => state`.
 */

import type { CdmEvent, EventLogState, DocumentLifecycleState } from "@/lib/sse/useSSE";
import { maxDocState, mapDocs } from "./helpers";

export function documentsReducer(state: EventLogState, ev: CdmEvent): EventLogState {
  switch (ev.eventType) {
    case "trade.ContractCaptured": {
      return {
        ...state,
        contract: {
          id:                 (ev.payload.contractId        as string) ?? "",
          incoterms:           ev.payload.incoterms          as string | undefined,
          hsCode:              ev.payload.hsCode             as string | undefined,
          commodity:           ev.payload.commodity          as string | undefined,
          quantityKg:          ev.payload.quantityKg         as number | undefined,
          priceUsd:            ev.payload.priceUsd           as number | undefined,
          deliveryWindowDays:  ev.payload.deliveryWindowDays as number | undefined,
        },
      };
    }
    case "document.Created": {
      const t = ev.payload.docType as string | undefined;
      if (!t) return state;
      const prev = state.documents[t];
      return {
        ...state,
        documents: {
          ...state.documents,
          [t]: {
            type: t,
            state: prev ? maxDocState(prev.state, "Created") : "Created",
            issuer: (ev.payload.issuer as string | undefined) ?? prev?.issuer,
            attestorOrbid: prev?.attestorOrbid,
            attestorLei: prev?.attestorLei,
          },
        },
      };
    }
    case "document.Hashed": {
      return {
        ...state,
        documents: mapDocs(state.documents, (d) => ({ ...d, state: maxDocState(d.state, "Hashed") })),
      };
    }
    case "document.Attested": {
      const t = ev.payload.docType as string | undefined;
      const att = ev.payload.attestorOrbid as string | undefined;
      if (!t) return state;
      const prev = state.documents[t] ?? { type: t, state: "Created" as DocumentLifecycleState };
      return {
        ...state,
        documents: {
          ...state.documents,
          [t]: { ...prev, state: maxDocState(prev.state, "Attested"), attestorOrbid: att, attestorLei: ev.payload.attestorLei as string | undefined },
        },
      };
    }
    case "document.BatchAttested": {
      const partners = (ev.payload.attestorOrbids as string[] | undefined) ?? [];
      return {
        ...state,
        documents: mapDocs(state.documents, (d, i) => ({
          ...d,
          state: maxDocState(d.state, "Attested"),
          attestorOrbid: d.attestorOrbid ?? partners[i % Math.max(1, partners.length)],
        })),
      };
    }
    case "document.Transmitted": {
      return {
        ...state,
        documents: mapDocs(state.documents, (d) => ({ ...d, state: maxDocState(d.state, "Transmitted") })),
      };
    }
    case "document.Validated": {
      return {
        ...state,
        documents: mapDocs(state.documents, (d) => ({ ...d, state: maxDocState(d.state, "Validated") })),
      };
    }
    case "document.ConsistencyReportProduced": {
      return {
        ...state,
        reconciliation: {
          ...state.reconciliation,
          status: "completed",
          reportId: ev.payload.reportId as string | undefined,
        },
      };
    }
    case "policy.Decided": {
      // Side-projection on the reconciliation matrix from doc-side
      // policy gates. The policy-log accumulation lives in the
      // compliance reducer.
      const gate = ev.payload.gate as string;
      const stage = ev.payload.stage as string | undefined;
      if (gate === "CrossDocumentConsistencyCheck") {
        return {
          ...state,
          reconciliation: { ...state.reconciliation, status: stage === "completed" ? "completed" : "started" },
        };
      }
      if (gate === "WeightToleranceRule") {
        return {
          ...state,
          reconciliation: {
            ...state.reconciliation,
            weightToleranceAutoResolved: true,
            cells: {
              ...state.reconciliation.cells,
              "PackingList::weight": "auto-resolved",
              "CommercialInvoice::weight": "auto-resolved",
            },
          },
        };
      }
      return state;
    }
    default:
      return state;
  }
}
